import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../../firebase.config';

// Bank templates configuration
const bankTemplates = {
  hdfc: {
    name: 'HDFC Bank',
    requiredFields: ['Date', 'Narration', 'Chq/Ref No.', 'Value Dt', 'Withdrawal Amt.', 'Deposit Amt.', 'Closing Balance'],
    transform: (row) => ({
      date: row['Date'],
      description: row['Narration'],
      type: row['Withdrawal Amt.'] ? 'Debit' : 'Credit',
      amount: row['Withdrawal Amt.'] || row['Deposit Amt.'] || 0,
      reference: row['Chq/Ref No.'] || ''
    })
  },
  sbi: {
    name: 'State Bank of India',
    requiredFields: ['Txn Date', 'Value Date', 'Description', 'Ref No./Cheque No.', 'Debit', 'Credit', 'Balance'],
    transform: (row) => ({
      date: row['Txn Date'],
      description: row['Description'],
      type: row['Debit'] ? 'Debit' : 'Credit',
      amount: row['Debit'] || row['Credit'] || 0,
      reference: row['Ref No./Cheque No.'] || ''
    })
  },
  axis: {
    name: 'AXIS Bank',
    requiredFields: ['Transaction Date', 'Value Date', 'Description', 'Ref No./Cheque No.', 'Withdrawal', 'Deposit', 'Balance'],
    transform: (row) => ({
      date: row['Transaction Date'],
      description: row['Description'],
      type: row['Withdrawal'] ? 'Debit' : 'Credit',
      amount: row['Withdrawal'] || row['Deposit'] || 0,
      reference: row['Ref No./Cheque No.'] || ''
    })
  },
  icici: {
    name: 'ICICI Bank',
    requiredFields: ['Txn Date', 'Value Date', 'Description', 'Ref No./Cheque No.', 'Debit', 'Credit', 'Balance'],
    transform: (row) => ({
      date: row['Txn Date'],
      description: row['Description'],
      type: row['Debit'] ? 'Debit' : 'Credit',
      amount: row['Debit'] || row['Credit'] || 0,
      reference: row['Ref No./Cheque No.'] || ''
    })
  },
  kotak: {
    name: 'Kotak Mahindra',
    requiredFields: ['Date', 'Particulars', 'Cheque Number', 'Withdraw', 'Deposit', 'Balance'],
    transform: (row) => ({
      date: row['Date'],
      description: row['Particulars'],
      type: row['Withdraw'] ? 'Debit' : 'Credit',
      amount: row['Withdraw'] || row['Deposit'] || 0,
      reference: row['Cheque Number'] || ''
    })
  },
  idfc: {
    name: 'IDFC First Bank',
    requiredFields: ['Transaction Date', 'Value Date', 'Description', 'Reference', 'Debit', 'Credit', 'Balance'],
    transform: (row) => ({
      date: row['Transaction Date'],
      description: row['Description'],
      type: row['Debit'] ? 'Debit' : 'Credit',
      amount: row['Debit'] || row['Credit'] || 0,
      reference: row['Reference'] || ''
    })
  },
  standard: {
    name: 'Standard Format',
    requiredFields: ['date', 'description', 'type', 'amount'],
    transform: (row) => ({
      date: row.date || new Date().toISOString().split('T')[0],
      description: row.description || '',
      type: ['Credit', 'Debit'].includes(row.type) ? row.type : 'Credit',
      amount: parseFloat(row.amount) || 0,
      branchName: row.branchName || '',
      accountName: row.accountName || '',
      accountNumber: row.accountNumber?.toString() || '',
      accountType: row.accountType || 'Saving'
    })
  }
};

const BulkImportModal = ({ open, onClose, tenantId, currentUser, onImported }) => {
  const [previewRows, setPreviewRows] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const [importProgress, setImportProgress] = useState(0);  
  const [fileHeaders, setFileHeaders] = useState([]);
  
  // Bank and branch states
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selectedBank, setSelectedBank] = useState('standard');
  
  // Mock data for banks and branches
  useEffect(() => {
    // Fetch banks from Firestore
    const fetchBanks = async () => {
      const { getDocs, collection } = await import('firebase/firestore');
      const querySnapshot = await getDocs(collection(firestore, 'AccountingBanks'));
      // Flatten branches for dropdown: { id, label, bankId, branchIdx, accountNumber }
      const banksData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const flatBranches = [];
      banksData.forEach(bank => {
        if (Array.isArray(bank.branches)) {
          bank.branches.forEach((branch, idx) => {
            flatBranches.push({
              id: `${bank.id}_${idx}`,
              bankId: bank.id,
              branchIdx: idx,
              label: `${bank.name} (${branch.ifsc})`,
              bankName: bank.name,
              ifsc: branch.ifsc,
              branchName: branch.branchName,
              accountNumber: branch.accountNumber || '',
              branchData: branch
            });
          });
        }
      });
      setBanks(banksData);
      setBranches(flatBranches);
    };
    fetchBanks();
  }, []);
  
  // Fetch branches when bank is selected
  // Remove old branch fetching logic, as branches are now flat

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Get headers for validation
        const headers = [];
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
          headers.push(cell ? cell.v : '');
        }
        setFileHeaders(headers);

        // Validate headers against selected bank template
        const bankTemplate = bankTemplates[selectedBank];
        const missingFields = bankTemplate.requiredFields.filter(
          field => !headers.includes(field)
        );

        if (missingFields.length > 0) {
          throw new Error(
            `The selected file doesn't match the ${bankTemplate.name} format. ` +
            `Missing required fields: ${missingFields.join(', ')}`
          );
        }

        const data = XLSX.utils.sheet_to_json(worksheet);
        
        // Transform data using the selected bank's template
        const transformedData = data.map(row => {
          const baseData = bankTemplate.transform(row);
          return {
            ...baseData,
            importedAt: serverTimestamp(),
            importedBy: currentUser.uid,
            tenantId,
            status: 'pending'
          };
        });

        setPreviewRows(transformedData.slice(0, 15)); // Show only first 15 rows in preview
        setError(null);
      } catch (err) {
        console.error('Error reading file:', err);
        setError('Error reading file. Please make sure it\'s a valid Excel/CSV file with correct columns.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const resetForm = () => {
    setPreviewRows([]);
    setError(null);
    setImportProgress(0);
    setFileHeaders([]);
  };

  const handleImport = async () => {
    if (!tenantId || !currentUser || previewRows.length === 0) {
      setError('Unable to import transactions. Please check your data and try again.');
      return;
    }

    let bankData = {};
    if (selectedBankId && selectedBranchId) {
      const selectedBranch = branches.find(b => b.id === selectedBranchId);
      if (selectedBranch) {
        bankData = {
          bankId: selectedBankId,
          branchId: selectedBranchId,
          branchName: selectedBranch.name,
          branchCode: selectedBranch.code
        };
      }
    }

    setIsImporting(true);
    setImportProgress(0);

    // Helper: Validate transaction fields
    function validateTransaction(row) {
      const errors = [];
      if (!row.date || isNaN(Date.parse(row.date))) errors.push('Invalid date');
      if (!row.amount || isNaN(Number(row.amount)) || Number(row.amount) <= 0) errors.push('Invalid amount');
      if (!['Credit', 'Debit'].includes(row.type)) errors.push('Invalid type');
      if (!row.accountNumber || row.accountNumber.length < 4) errors.push('Invalid account number');
      if (!row.accountType) errors.push('Missing account type');
      // Add more field checks as needed
      return errors;
    }

    // Group by accountId/bankId
    const groupKey = (row) => `${row.accountNumber || ''}_${row.bankId || bankData.bankId || ''}`;
    const grouped = {};
    previewRows.forEach(row => {
      const key = groupKey(row);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });

    // Summary response
    const summary = {
      totalRows: previewRows.length,
      processedRows: 0,
      failedRows: [],
      batchesCommitted: 0,
      timeTaken: 0
    };
    const startTime = Date.now();

    try {
      // For each accountId/bankId group, check Firestore count
      for (const key of Object.keys(grouped)) {
        const rows = grouped[key];
        const [accountNumber, bankId] = key.split('_');
        // Query Firestore for count using modular SDK
        const q = collection(firestore, 'Accounting Transactions Master');
        // Use Firestore query with where filters
        const { getDocs, writeBatch, doc, serverTimestamp, query, where } = await import('firebase/firestore');
        const countQuery = query(q,
          where('tenantId', '==', tenantId),
          where('accountNumber', '==', accountNumber),
          where('bankId', '==', bankId)
        );
        const querySnapshot = await getDocs(countQuery);
        const existingCount = querySnapshot.size;
        if (existingCount + rows.length > 10000) {
          setError(`Transaction limit exceeded: Max 10,000 records allowed per ID.`);
          summary.failedRows.push({ key, error: 'Transaction limit exceeded' });
          continue;
        }

        // Validate all rows
        const validRows = [];
        rows.forEach((row, idx) => {
          const errors = validateTransaction(row);
          if (errors.length) {
            summary.failedRows.push({ row, errors });
          } else {
            validRows.push(row);
          }
        });

        // Chunk into 500
        for (let i = 0; i < validRows.length; i += 500) {
          const batchRows = validRows.slice(i, i + 500);
          const batch = writeBatch(firestore);
          batchRows.forEach(row => {
            const docRef = doc(q);
            batch.set(docRef, {
              ...row,
              ...bankData,
              createdBy: currentUser?.uid || currentUser?.email || '',
              createdAt: serverTimestamp(),
              tenantId
            });
          });
          await batch.commit();
          summary.batchesCommitted++;
          summary.processedRows += batchRows.length;
          setImportProgress(Math.min(100, ((summary.processedRows) / previewRows.length) * 100));
        }
      }
      summary.timeTaken = Date.now() - startTime;

      resetForm();
      setError({ severity: 'success', message: `Import complete. ${summary.processedRows} processed, ${summary.failedRows.length} failed.` });
      onImported?.(summary);
    } catch (err) {
      console.error('Error importing transactions:', err);
      setError({ severity: 'error', message: 'Failed to import transactions. Please try again.' });
    } finally {
      setIsImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [{
      date: new Date().toISOString().split('T')[0],
      description: 'Sample Transaction',
      type: 'Credit',
      amount: '1000.00',
      branchName: 'HDFC Bank',
      accountName: 'John Doe',
      accountNumber: '1234567890',
      accountType: 'Saving'
    }];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'template');
    XLSX.writeFile(wb, 'transactions-template.xlsx');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Import Transactions</DialogTitle>
      <DialogContent>
        {error && (
          <Alert 
            severity={error.severity || 'error'} 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error.message || error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 350 }}>
            <InputLabel id="bank-branch-select-label">Select Bank & IFSC</InputLabel>
            <Select
              labelId="bank-branch-select-label"
              value={selectedBranchId}
              label="Select Bank & IFSC"
              onChange={(e) => setSelectedBranchId(e.target.value)}
              disabled={isImporting}
            >
              <MenuItem value=""><em>Select Bank & IFSC</em></MenuItem>
              {branches.map(branch => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.label}
                  {branch.accountNumber ? ` - ${branch.accountNumber}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={isImporting}
          >
            Upload Bank Statement
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              disabled={isImporting}
            />
          </Button>
          <Button variant="outlined" onClick={downloadTemplate}>Download Template</Button>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview showing {previewRows.length > 0 ? `first ${Math.min(previewRows.length, 15)} of ${previewRows.length}` : '0'} rows
            </Typography>
          </Box>
        </Box>

        {previewRows.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Bank & IFSC</TableCell>
                <TableCell>Account Number</TableCell>
                {selectedBank !== 'standard' && <TableCell>Reference</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.slice(0, 100).map((row, index) => (
                <TableRow 
                  key={index}
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell>{row.date}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} 
                            title={row.description}>
                    {row.description}
                  </TableCell>
                  <TableCell>
                    <span style={{ 
                      color: row.type === 'Credit' ? 'green' : 'red',
                      fontWeight: 'bold'
                    }}>
                      {row.type}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    {row.amount.toLocaleString('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      minimumFractionDigits: 2
                    })}
                  </TableCell>
                  <TableCell>
                    {selectedBranchId ?
                      branches.find(b => b.id === selectedBranchId)?.label || '-' : '-'}
                  </TableCell>
                  <TableCell>
                    {selectedBranchId ?
                      branches.find(b => b.id === selectedBranchId)?.accountNumber || '-' : '-'}
                  </TableCell>
                  {selectedBank !== 'standard' && <TableCell>{row.reference || '-'}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Upload an Excel/CSV file to preview transactions here.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isImporting}> 
          Cancel
        </Button>
        <Button 
          variant="contained"   
          onClick={handleImport} 
          disabled={previewRows.length === 0 || isImporting}
          startIcon={isImporting ? <CircularProgress size={20} /> : null}
          color="success"
        >
          {isImporting 
            ? `Importing... ${importProgress}%` 
            : `Import ${previewRows.length} Transactions`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportModal;

